package com.trevorwiebe.timesheet.admin.locations.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.domain.GeocodePoint
import com.trevorwiebe.timesheet.core.domain.GeocodingRepository
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class LocationsViewModel(
    private val locationRepository: LocationRepository,
    private val geocodingRepository: GeocodingRepository
) : DisposableViewModel() {

    private val _state = MutableStateFlow(LocationsState())
    val state = _state.asStateFlow()

    init {
        loadLocations()
    }

    fun onEvent(event: LocationsEvent) {
        when (event) {
            is LocationsEvent.OnAddLocationClick -> {
                _state.update {
                    it.copy(
                        showAddEditDialog = true,
                        editingLocation = null,
                        form = LocationFormState(),
                        geocodeError = null
                    )
                }
            }
            is LocationsEvent.OnEditLocationClick -> {
                _state.update {
                    it.copy(
                        showAddEditDialog = true,
                        editingLocation = event.location,
                        form = LocationFormState(
                            name = event.location.name,
                            city = event.location.city,
                            state = event.location.state,
                            type = event.location.type,
                            address = event.location.address,
                            latitude = event.location.latitude,
                            longitude = event.location.longitude
                        ),
                        geocodeError = null
                    )
                }
            }
            is LocationsEvent.OnDismissDialog -> {
                _state.update {
                    it.copy(
                        showAddEditDialog = false,
                        editingLocation = null,
                        form = LocationFormState(),
                        geocodeError = null
                    )
                }
            }
            is LocationsEvent.OnFormNameChanged -> updateForm { it.copy(name = event.value) }
            is LocationsEvent.OnFormCityChanged -> updateForm { it.copy(city = event.value) }
            is LocationsEvent.OnFormStateChanged -> updateForm { it.copy(state = event.value) }
            is LocationsEvent.OnFormTypeChanged -> updateForm { it.copy(type = event.value) }
            is LocationsEvent.OnFormAddressChanged -> updateForm { it.copy(address = event.value) }
            is LocationsEvent.OnClearCoordinates -> {
                updateForm { it.copy(latitude = null, longitude = null) }
            }
            is LocationsEvent.OnSaveLocation -> {
                saveLocation()
            }
            is LocationsEvent.OnGeocodeAddress -> {
                geocodeAddress(openMapPickerAfter = false)
            }
            is LocationsEvent.OnOpenMapPicker -> {
                openMapPicker()
            }
            is LocationsEvent.OnCloseMapPicker -> {
                _state.update { it.copy(showMapPicker = false) }
            }
            is LocationsEvent.OnPinConfirmed -> {
                _state.update {
                    it.copy(
                        showMapPicker = false,
                        form = it.form.copy(latitude = event.latitude, longitude = event.longitude)
                    )
                }
            }
            is LocationsEvent.OnDeleteLocation -> {
                deleteLocation()
            }
            is LocationsEvent.OnRestoreLocation -> {
                restoreLocation()
            }
            is LocationsEvent.OnSearchQueryChanged -> {
                _state.update { it.copy(searchQuery = event.value) }
            }
            is LocationsEvent.OnFilterToggled -> {
                _state.update { state ->
                    val current = state.activeFilters
                    val updated = if (event.filter in current) current - event.filter else current + event.filter
                    state.copy(activeFilters = updated)
                }
            }
            is LocationsEvent.OnEnterSelectionMode -> {
                _state.update {
                    it.copy(selectionMode = true, selectedIds = setOf(event.location.id))
                }
            }
            is LocationsEvent.OnExitSelectionMode -> {
                _state.update { it.copy(selectionMode = false, selectedIds = emptySet()) }
            }
            is LocationsEvent.OnToggleSelection -> {
                toggleSelection(event.location)
            }
            is LocationsEvent.OnMergeClick -> {
                if (_state.value.selectedIds.size >= 2) {
                    _state.update { it.copy(showMergeDialog = true) }
                }
            }
            is LocationsEvent.OnDismissMergeDialog -> {
                _state.update { it.copy(showMergeDialog = false) }
            }
            is LocationsEvent.OnConfirmMerge -> {
                mergeLocations(event.keeperId)
            }
        }
    }

    private fun toggleSelection(location: Location) {
        _state.update { state ->
            // Enforce same-type merges: a location of a different type than the
            // current selection can't be added.
            val lockedType = state.selectedType
            if (location.id !in state.selectedIds && lockedType != null && location.type != lockedType) {
                return@update state
            }
            val updated = if (location.id in state.selectedIds) {
                state.selectedIds - location.id
            } else {
                state.selectedIds + location.id
            }
            // Leaving selection empty drops out of selection mode entirely.
            if (updated.isEmpty()) {
                state.copy(selectionMode = false, selectedIds = emptySet())
            } else {
                state.copy(selectedIds = updated)
            }
        }
    }

    private fun mergeLocations(keeperId: String) {
        viewModelScope.launch {
            val selectedIds = _state.value.selectedIds
            val absorbedIds = selectedIds.filter { it != keeperId }
            if (absorbedIds.isEmpty()) {
                _state.update { it.copy(showMergeDialog = false) }
                return@launch
            }
            _state.update { it.copy(isMerging = true) }
            val result = locationRepository.mergeLocations(keeperId, absorbedIds)
            if (result.error != null) {
                _state.update { it.copy(isMerging = false, error = result.error) }
            } else {
                _state.update {
                    it.copy(
                        isMerging = false,
                        showMergeDialog = false,
                        selectionMode = false,
                        selectedIds = emptySet()
                    )
                }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLocations() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val flow = locationRepository.getLocations()
            flow.collect { result ->
                if (result.error != null) {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                } else {
                    val locations = result.data as List<Location>
                    _state.update { it.copy(isLoading = false, locations = locations, error = null) }
                }
            }
        }
    }

    // Any edit invalidates the previous validation/geocode errors.
    private fun updateForm(transform: (LocationFormState) -> LocationFormState) {
        _state.update {
            it.copy(form = transform(it.form).copy(validationError = null), geocodeError = null)
        }
    }

    private fun saveLocation() {
        val form = _state.value.form
        val missing = listOfNotNull(
            "Name".takeIf { form.name.isBlank() },
            "City".takeIf { form.city.isBlank() },
            "State".takeIf { form.state.isBlank() }
        )
        if (missing.isNotEmpty()) {
            _state.update {
                it.copy(
                    form = it.form.copy(
                        validationError = "Please enter ${missing.joinToString(", ")} before saving."
                    )
                )
            }
            return
        }
        viewModelScope.launch {
            val editing = _state.value.editingLocation
            if (editing == null) {
                locationRepository.addLocation(
                    Location(
                        id = "",
                        name = form.name.trim(),
                        city = form.city.trim(),
                        state = form.state.trim(),
                        type = form.type,
                        deleted = false,
                        address = form.address.trim(),
                        latitude = form.latitude,
                        longitude = form.longitude
                    )
                )
            } else {
                locationRepository.updateLocation(
                    editing.copy(
                        name = form.name.trim(),
                        city = form.city.trim(),
                        state = form.state.trim(),
                        type = form.type,
                        address = form.address.trim(),
                        latitude = form.latitude,
                        longitude = form.longitude
                    )
                )
            }
            _state.update {
                it.copy(showAddEditDialog = false, editingLocation = null, form = LocationFormState())
            }
        }
    }

    /**
     * Geocodes the in-progress form's address (falling back to city/state) and
     * writes the result into the form. Optionally opens the map picker afterward —
     * even when geocoding fails, so the user can drop the pin manually.
     */
    private fun geocodeAddress(openMapPickerAfter: Boolean) {
        val form = _state.value.form
        val query = listOf(form.address, form.city, form.state)
            .filter { it.isNotBlank() }
            .joinToString(", ")
        if (query.isBlank()) {
            _state.update { it.copy(geocodeError = "Enter an address first") }
            return
        }
        viewModelScope.launch {
            _state.update { it.copy(isGeocoding = true, geocodeError = null) }
            val result = geocodingRepository.geocode(query)
            val point = result.data as? GeocodePoint
            _state.update {
                it.copy(
                    isGeocoding = false,
                    geocodeError = result.error,
                    showMapPicker = if (openMapPickerAfter) true else it.showMapPicker,
                    form = if (point != null) {
                        it.form.copy(latitude = point.latitude, longitude = point.longitude)
                    } else it.form
                )
            }
        }
    }

    private fun openMapPicker() {
        val form = _state.value.form
        val hasSearchText = listOf(form.address, form.city, form.state).any { it.isNotBlank() }
        if (form.latitude == null && hasSearchText) {
            geocodeAddress(openMapPickerAfter = true)
        } else {
            _state.update { it.copy(showMapPicker = true) }
        }
    }

    private fun deleteLocation() {
        viewModelScope.launch {
            val editing = _state.value.editingLocation ?: return@launch
            locationRepository.updateLocation(editing.copy(deleted = true))
            _state.update {
                it.copy(showAddEditDialog = false, editingLocation = null, form = LocationFormState())
            }
        }
    }

    private fun restoreLocation() {
        viewModelScope.launch {
            val editing = _state.value.editingLocation ?: return@launch
            locationRepository.updateLocation(editing.copy(deleted = false))
            _state.update {
                it.copy(showAddEditDialog = false, editingLocation = null, form = LocationFormState())
            }
        }
    }
}
